import React from 'react';
import { Loader2 } from 'lucide-react';
import apiService from '../apiService';
import { useAuth } from '../context/AuthContext';
import ConnectAssistantPage from '../components/connect/ConnectAssistantPage';
import { mcpServerUrl } from '../components/connect/connectInstructions';
import { useShellUiActions } from '../context/ShellUiContext';
import { usesChatFree } from '../utils/moduleAccess';
import { consumeInviteWelcome } from '../utils/inviteWelcome';

/** The page with its Chat entry. Split out so only an account that has Chat
 *  here needs the shell's navigation. */
function ConnectWithChat(props: React.ComponentProps<typeof ConnectAssistantPage>) {
    const { navigateToView } = useShellUiActions();
    return <ConnectAssistantPage {...props} onOpenChat={() => navigateToView('lookup')} />;
}

/**
 * `/connect`: how to connect BARK's MCP server to an AI assistant.
 *
 * NOT module-gated, on purpose, like /billing. A free (MCP-only) account has
 * every web module off, and this is the page it lands on. So the page calls no
 * orchestrator or report-library route: those refuse such an account (403
 * `web_access_not_in_plan`). The only request it can make is config.json, and
 * only when nothing has read it yet (a sign-in or a token refresh reads it
 * first, so that is rare).
 */
export default function ConnectRoute() {
    const { userData } = useAuth();
    const [welcome] = React.useState(() => consumeInviteWelcome());
    const [gateway, setGateway] = React.useState(() => apiService.getMcpGatewayUrl());
    const [configPending, setConfigPending] = React.useState(() => !apiService.getMcpGatewayUrl());
    // A config that did not load is not the same as one that names no gateway:
    // the page still shows the production server, but says why, so a dev user
    // is never sent to prod without a word.
    const [configFailed, setConfigFailed] = React.useState(false);

    React.useEffect(() => {
        if (!configPending) return;
        let cancelled = false;
        apiService
            .initialize()
            // A config that does not load leaves the gateway empty, and the
            // page then shows the production server, with a note.
            .catch(() => {
                if (!cancelled) setConfigFailed(true);
            })
            .then(() => {
                if (cancelled) return;
                setGateway(apiService.getMcpGatewayUrl());
                setConfigPending(false);
            });
        return () => {
            cancelled = true;
        };
    }, [configPending]);

    if (configPending) {
        return (
            <div className="flex h-full items-center justify-center bg-bg">
                <div role="status" className="flex items-center gap-2 font-body text-[13px] text-fg-secondary">
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} aria-hidden="true" />
                    Loading the connection details…
                </div>
            </div>
        );
    }

    const configNote = configFailed && !gateway
        ? 'BARK could not load the settings for this environment, so this is the production server. Reload the page to try again.'
        : undefined;
    const Page = usesChatFree(userData) ? ConnectWithChat : ConnectAssistantPage;
    return (
        <Page
            serverUrl={mcpServerUrl(gateway)}
            email={userData?.email}
            configNote={configNote}
            welcome={welcome}
        />
    );
}
